/**
 * Authentication state store using Zustand.
 * Manages user session, login/logout, and authentication state with session-based auth.
 * Uses httpOnly cookies for secure session management (no tokens stored in frontend).
 */

import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { api } from '@/services/api';
import type { User, LoginRequest, PasswordChangeRequest, ApiError } from '@/types/auth';
import axios from 'axios';

interface AuthState {
  // State
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  mustChangePassword: boolean;

  // Actions
  login: (credentials: LoginRequest) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  changePassword: (passwordRequest: PasswordChangeRequest) => Promise<void>;
  clearError: () => void;
  setUser: (user: User) => void;
}

/**
 * Extract error message from API error response
 */
const getErrorMessage = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    const apiError = error.response?.data as ApiError | undefined;
    return apiError?.detail || error.message || 'An unexpected error occurred';
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'An unexpected error occurred';
};

export const useAuthStore = create<AuthState>()(
  immer((set) => ({
    // Initial state
    user: null,
    isAuthenticated: false,
    isLoading: false,
    error: null,
    mustChangePassword: false,

    /**
     * Login user with username and password.
     * Sets session cookie via API (httpOnly, secure).
     */
    login: async (credentials: LoginRequest) => {
      set((state) => {
        state.isLoading = true;
        state.error = null;
      });

      try {
        const response = await api.login(credentials);

        set((state) => {
          state.user = response.user;
          state.isAuthenticated = true;
          state.mustChangePassword = response.must_change_password;
          state.isLoading = false;
          state.error = null;
        });
      } catch (error) {
        const errorMessage = getErrorMessage(error);

        set((state) => {
          state.user = null;
          state.isAuthenticated = false;
          state.mustChangePassword = false;
          state.isLoading = false;
          state.error = errorMessage;
        });

        throw error; // Re-throw so components can handle it
      }
    },

    /**
     * Logout user and clear session cookie.
     */
    logout: async () => {
      set((state) => {
        state.isLoading = true;
        state.error = null;
      });

      try {
        await api.logout();

        set((state) => {
          state.user = null;
          state.isAuthenticated = false;
          state.mustChangePassword = false;
          state.isLoading = false;
          state.error = null;
        });
      } catch (error) {
        const errorMessage = getErrorMessage(error);

        // Even if logout fails, clear local state
        set((state) => {
          state.user = null;
          state.isAuthenticated = false;
          state.mustChangePassword = false;
          state.isLoading = false;
          state.error = errorMessage;
        });

        console.error('Logout error:', errorMessage);
      }
    },

    /**
     * Check if user has valid session.
     * Called on app initialization and after page refresh.
     */
    checkAuth: async () => {
      set((state) => {
        state.isLoading = true;
        state.error = null;
      });

      try {
        const user = await api.getCurrentUser();

        set((state) => {
          state.user = user;
          state.isAuthenticated = true;
          state.mustChangePassword = user.must_change_password;
          state.isLoading = false;
          state.error = null;
        });
      } catch (error) {
        // If session is invalid, clear auth state (don't set error for initial check)
        set((state) => {
          state.user = null;
          state.isAuthenticated = false;
          state.mustChangePassword = false;
          state.isLoading = false;
          state.error = null; // Don't show error on initial auth check failure
        });
      }
    },

    /**
     * Change user password.
     * Clears must_change_password flag on success.
     * Note: Backend invalidates ALL sessions after password change,
     * so user will need to log in again.
     */
    changePassword: async (passwordRequest: PasswordChangeRequest) => {
      set((state) => {
        state.isLoading = true;
        state.error = null;
      });

      try {
        await api.changePassword(passwordRequest);

        // Password change invalidates ALL sessions (including current one)
        // Clear auth state - user will need to log in again
        set((state) => {
          state.user = null;
          state.isAuthenticated = false;
          state.mustChangePassword = false;
          state.isLoading = false;
          state.error = null;
        });
      } catch (error) {
        const errorMessage = getErrorMessage(error);

        set((state) => {
          state.isLoading = false;
          state.error = errorMessage;
        });

        throw error; // Re-throw so components can handle it
      }
    },

    /**
     * Clear error message.
     */
    clearError: () => {
      set((state) => {
        state.error = null;
      });
    },

    /**
     * Set user directly (useful for testing or manual updates).
     */
    setUser: (user: User) => {
      set((state) => {
        state.user = user;
        state.isAuthenticated = true;
        state.mustChangePassword = user.must_change_password;
      });
    },
  }))
);

/**
 * Helper to check if user has a specific role.
 */
export const useHasRole = (roleName: string): boolean => {
  const user = useAuthStore((state) => state.user);
  return user?.roles?.some((role) => role.name === roleName) ?? false;
};

/**
 * Helper to check if user has admin role.
 */
export const useIsAdmin = (): boolean => {
  return useHasRole('admin');
};

/**
 * Helper to check if user has engineer or admin role.
 */
export const useIsEngineerOrAdmin = (): boolean => {
  const user = useAuthStore((state) => state.user);
  return (
    user?.roles?.some((role) => role.name === 'admin' || role.name === 'engineer') ?? false
  );
};
